import React from 'react';
import { motion } from 'framer-motion';

const Metrics = () => {
  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      className="relative py-20 px-4 md:px-8 overflow-hidden font-sans"
      style={{
        background: 'linear-gradient(180deg, #F0F9FF 0%, #FFFFFF 100%)'
      }}
    >
      
      {/* Decorative Blur */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-sky-200 to-transparent opacity-50"></div>

      {/* Main Content */}
      <motion.div 
        className="relative z-10 max-w-7xl min-h-[70vh] mx-auto"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        
        {/* Section Badge */}
        <motion.div className="flex justify-center mb-6" variants={itemVariants}>
          <div className="inline-flex bg-white/50 backdrop-blur-sm items-center justify-center px-8 py-1.5 border border-gray-300 rounded-full shadow-sm">
            <span className="text-gray-600 text-lg font-normal">Métricas</span>
          </div>
        </motion.div>

        {/* Section Title */}
        <motion.h2 
          className="text-3xl md:text-5xl font-bold text-[#1D4D73] text-center mb-16 leading-tight"
          variants={itemVariants}
        >
          Transformando futuros
        </motion.h2>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          
          {/* Card 1: 320+ */}
          <motion.div 
            className="bg-white rounded-[2rem] p-8 flex flex-col justify-between h-full shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
            variants={itemVariants}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-6xl font-bold text-[#1a3b5c] tracking-tighter">10+</span>
              {/* Graduation Cap Icon */}
              <svg className="w-11 h-11 text-[#5B9BB0] flex-shrink-0" fill="currentColor" viewBox="0 0 640 512">
                <path d="M320 32c-8.1 0-16.1 1.4-23.7 4.1L15.8 137.4C6.3 140.9 0 149.9 0 160s6.3 19.1 15.8 22.6l57.9 20.9C57.3 229.3 48 259.8 48 291.9v28.1c0 28.4-10.8 57.7-22.3 80.8c-6.5 13-13.9 25.8-22.5 37.6C0 442.7-.9 448.3 .9 453.4s6 8.9 11.2 10.2l64 16c4.2 1.1 8.7 .3 12.4-2s6.3-6.1 7.1-10.4c8.6-42.8 4.3-81.2-2.1-108.7C90.3 344.3 86 329.8 80 316.5V291.9c0-30.2 10.2-58.7 27.9-81.5c12.9-15.5 29.6-28 49.2-35.7l157-61.7c8.2-3.2 17.5 .8 20.7 9s-.8 17.5-9 20.7l-157 61.7c-12.4 4.9-23.3 12.4-32.2 21.6l159.6 57.6c7.6 2.7 15.6 4.1 23.7 4.1s16.1-1.4 23.7-4.1L624.2 182.6c9.5-3.4 15.8-12.5 15.8-22.6s-6.3-19.1-15.8-22.6L343.7 36.1C336.1 33.4 328.1 32 320 32zM128 408c0 35.3 86 72 192 72s192-36.7 192-72L496.7 262.6 354.5 314c-11.1 4-22.8 6-34.5 6s-23.5-2-34.5-6L143.3 262.6 128 408z"/>
              </svg>
            </div>
            <p className="text-gray-600 text-[0.95rem] leading-snug font-medium">
              Años de Experiencia en Educación.
            </p>
          </motion.div>

          {/* Card 2: 14+ */}
          <motion.div 
            className="bg-white rounded-[2rem] p-8 flex flex-col justify-between h-full shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
            variants={itemVariants}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-6xl font-bold text-[#1a3b5c] tracking-tighter">360+</span>
              {/* Users Icon */}
              <svg className="w-11 h-11 text-[#5B9BB0] flex-shrink-0" fill="currentColor" viewBox="0 0 640 512">
                <path d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192h42.7c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96c-.2 0-.4 0-.7 0H21.3C9.6 320 0 310.4 0 298.7zM405.3 320c-.2 0-.4 0-.7 0c26.6-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7h42.7C592.2 192 640 239.8 640 298.7c0 11.8-9.6 21.3-21.3 21.3H405.3zM224 224a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM128 485.3C128 411.7 187.7 352 261.3 352H378.7C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7H154.7c-14.7 0-26.7-11.9-26.7-26.7z"/>
              </svg>
            </div>
            <p className="text-gray-600 text-[0.95rem] leading-snug font-medium">
              Estudiantes que han participado en MUN.
            </p>
          </motion.div>

          {/* Card 3: 10+ */}
          <motion.div 
            className="bg-white rounded-[2rem] p-8 flex flex-col justify-between h-full shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
            variants={itemVariants}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-6xl font-bold text-[#1a3b5c] tracking-tighter">30+</span>
              {/* Globe Icon */}
              <svg className="w-10 h-10 text-[#5B9BB0] flex-shrink-0" fill="currentColor" viewBox="0 0 512 512">
                <path d="M352 256c0 22.2-1.2 43.6-3.3 64H163.3c-2.2-20.4-3.3-41.8-3.3-64s1.2-43.6 3.3-64H348.7c2.2 20.4 3.3 41.8 3.3 64zm28.8-64H503.9c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64H380.8c2.1-20.6 3.2-42 3.2-64s-1.1-43.4-3.2-64zm112.6-32H376.7c-10-63.9-29.8-117.4-55.3-151.6c78.3 20.7 142 77.5 171.9 151.6zm-149.1 0H167.7c6.1-36.4 15.5-68.6 27-94.7c10.5-23.6 22.2-40.7 33.5-51.5C239.4 3.2 248.7 0 256 0s16.6 3.2 27.8 13.8c11.3 10.8 23 27.9 33.5 51.5c11.6 26 20.9 58.2 27 94.7zm-209 0H18.6C48.6 85.9 112.2 29.1 190.6 8.4C165.1 42.6 145.3 96.1 135.3 160zM8.1 192H131.2c-2.1 20.6-3.2 42-3.2 64s1.1 43.4 3.2 64H8.1C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM194.7 446.6c-11.6-26-20.9-58.2-27-94.6H344.3c-6.1 36.4-15.5 68.6-27 94.6c-10.5 23.6-22.2 40.7-33.5 51.5C272.6 508.8 263.3 512 256 512s-16.6-3.2-27.8-13.8c-11.3-10.8-23-27.9-33.5-51.5zM135.3 352c10 63.9 29.8 117.4 55.3 151.6C112.2 482.9 48.6 426.1 18.6 352H135.3zm358.1 0c-30 74.1-93.6 130.9-171.9 151.6c25.5-34.2 45.2-87.7 55.3-151.6H493.4z"/>
              </svg>
            </div>
            <p className="text-gray-600 text-[0.95rem] leading-snug font-medium">
              MUN Awards obtenidos nacional e internacionalmente.
            </p>
          </motion.div>

          {/* Card 4: 188+ */}
          <motion.div 
            className="bg-white rounded-[2rem] p-8 flex flex-col justify-between h-full shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
            variants={itemVariants}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
          >
            <div className="flex items-start justify-between mb-4">
              <span className="text-6xl font-bold text-[#1a3b5c] tracking-tighter">5+</span>
              {/* School Building Icon */}
              <svg className="w-10 h-10 text-[#5B9BB0] flex-shrink-0" fill="currentColor" viewBox="0 0 640 512">
                <path d="M337.8 5.4C327-1.8 313-1.8 302.2 5.4L166.3 96H48C21.5 96 0 117.5 0 144V464c0 26.5 21.5 48 48 48H256V416c0-35.3 28.7-64 64-64s64 28.7 64 64v96H592c26.5 0 48-21.5 48-48V144c0-26.5-21.5-48-48-48H473.7L337.8 5.4zM96 192h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H96c-8.8 0-16-7.2-16-16V208c0-8.8 7.2-16 16-16zm0 128h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H96c-8.8 0-16-7.2-16-16V336c0-8.8 7.2-16 16-16zM224 192h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H224c-8.8 0-16-7.2-16-16V208c0-8.8 7.2-16 16-16zm0 128h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H224c-8.8 0-16-7.2-16-16V336c0-8.8 7.2-16 16-16zM480 192h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H480c-8.8 0-16-7.2-16-16V208c0-8.8 7.2-16 16-16zm0 128h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H480c-8.8 0-16-7.2-16-16V336c0-8.8 7.2-16 16-16z"/>
              </svg>
            </div>
            <p className="text-gray-600 text-[0.95rem] leading-snug font-medium">
              Años liderando proyectos educativos de alcance internacional.
            </p>
          </motion.div>
        </div>

        {/* Footer Text */}
        <motion.p 
          className="text-center text-[#1a3b5c] text-lg font-medium max-w-4xl mx-auto leading-relaxed"
          variants={itemVariants}
        >
          Nuestro propósito es transformar la educación desde dentro, dotando a cada institución aliada de herramientas, estrategias y programas que preparen a los estudiantes para un mundo competitivo, multicultural y en constante evolución.
        </motion.p>
      </motion.div>
    </section>
  );
};

export default Metrics;